import type { Connector } from "@/hooks/useConnectors";

// Kept out of the dialog because it is pure decision logic, not rendering: what pressing a connector
// should do is the part worth testing directly, and a component file exporting helpers also breaks
// fast refresh.

export type ConnectorAction =
  | { kind: "modal"; connector: string }
  | { kind: "oauth"; connector: string }
  | { kind: "credentials"; connector: string }
  | { kind: "prompt"; connector: string; prompt: string }
  | { kind: "blocked"; connector: string; reason: string };

/**
 * Hand-written copy for the project-scoped connectors we ship.
 *
 * No longer a gate on which connectors work — it is a refinement of the wording. Anything
 * project-scoped that is not listed falls through to a generic prompt built from its name, so a
 * connector added on the server behaves correctly before anyone writes copy for it.
 */
const PROJECT_SCOPED_PROMPTS: Record<string, string> = {
  stripe: "Create a new project with Stripe payments enabled.",
  joylo_sso: "Create a new project with sign-in enabled.",
};

const promptFor = (connector: Connector): string =>
  PROJECT_SCOPED_PROMPTS[connector.key] ??
  `Create a new project with ${connector.name} set up.`;

/**
 * What pressing a connector should do.
 *
 * Decided from the connector's own reported shape rather than a switch on its key, so a connector
 * added on the server gets correct behaviour with no client change. That switch is the bug this
 * replaced: it handled two keys, and every other connector rendered a button that did nothing.
 *
 * Account-level connectors open their real setup flow. Payments and sign-in are configured per
 * project, so away from one they become a written prompt — both honest about why a settings screen
 * is unavailable and a shorter path to what the user actually wanted.
 */
export const actionFor = (connector: Connector, projectId?: string): ConnectorAction => {
  if (connector.available === false) {
    return {
      kind: "blocked",
      connector: connector.key,
      reason: `${connector.name} is switched off for this workspace. An owner or admin can turn it back on.`,
    };
  }

  if (connector.scope === "project" && !projectId) {
    return { kind: "prompt", connector: connector.key, prompt: promptFor(connector) };
  }

  // Declared credential fields mean this one is configured by typing details in, not by a grant.
  // Decided from the schema rather than the key so a connector added server-side — Twilio, a
  // self-hosted API — gets a working form with no client change.
  if (connector.credentialFields && connector.credentialFields.length > 0) {
    return { kind: "credentials", connector: connector.key };
  }

  // Connectors on the generic OAuth routes are connected by the dialog itself. Falling through to
  // "modal" is what made Gmail do nothing: the host only knows the connectors that predate the
  // framework, so anything newer closed the dialog and stopped there.
  if (connector.usesGenericOauth) {
    return { kind: "oauth", connector: connector.key };
  }

  return { kind: "modal", connector: connector.key };
};

/**
 * A payload field name nobody wrote copy for, made readable.
 *
 * The permission dialog has to describe a call the user is deciding whether to allow, for services
 * that do not exist yet. A raw camelCase key at that moment is the worst possible time to be
 * unreadable, so an unknown field is humanised rather than printed as-is.
 */
export const humaniseField = (name: string): string => {
  const spaced = name
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();
  if (!spaced) return name;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};

/** Label for a connector's primary button, preferring the server's own wording. */
export const ctaLabelFor = (connector: Connector): string => {
  if (connector.ctaLabel) return connector.ctaLabel;
  return connector.connected ? "Manage" : "Connect";
};

/**
 * Whether a connector matches a search term.
 *
 * Searches tool names as well as the connector's own text, so "send email" finds Gmail and
 * "atlassian" finds Jira — the terms people actually reach for are often the vendor or the action,
 * not the product name we happen to display.
 */
export const matchesSearch = (connector: Connector, term: string): boolean => {
  const q = term.trim().toLowerCase();
  if (!q) return true;

  const haystack = [
    connector.name,
    connector.summary,
    connector.key,
    connector.provider ?? "",
    ...(connector.categories ?? []),
    ...(connector.tools ?? []).flatMap((t) => [t.name, t.title]),
  ]
    .join(" ")
    .toLowerCase();

  return q.split(/\s+/).every((word) => haystack.includes(word));
};
